import axios from "axios";
import i18n from "i18next";
import { getCookie } from "typescript-cookie";
import {
  API_EVENTS_LIST_PAGE_SIZE,
  API_EXPENSES_LIST_PAGE_SIZE,
  API_PAYMENTS_LIST_PAGE_SIZE,
  API_ORDERS_LIST_PAGE_SIZE,
  API_PRODUCTS_LIST_PAGE_SIZE,
  API_ADMIN_USER_LIST_PAGE_SIZE,
  API_ADMIN_ORDER_LIST_PAGE_SIZE,
  API_MEDIA_PRESS_LIST_PAGE_SIZE,
  API_ADMIN_EVENT_LIST_PAGE_SIZE,
  API_DOCUMENTS_LIST_PAGE_SIZE,
  API_ADMIN_TOWERS_STATS_POSITIONS_PAGE_SIZE,
  API_LEGAL_GROUP_LIST_PAGE_SIZE,
  API_HISTORY_EVENT_LIST_PAGE_SIZE,
  API_ADMIN_HISTORY_EVENT_PAGE_SIZE,
  API_ADMIN_REGISTRATION_LIST_PAGE_SIZE,
  API_REGISTRATIONS_LIST_PAGE_SIZE,
  API_PROGRAM_COURSE_REGISTRATIONS_LIST_PAGE_SIZE,
  API_NEWSLETTER_LIST_PAGE_SIZE,
  API_CONSENT_ENTITY_LIST_PAGE_SIZE,
  API_ADMIN_THEME_ICON_PAGE_SIZE,
  API_ADMIN_EVENT_REGISTRATION_PAGE_SIZE,
} from "../consts";
import {
  ContactMessageType,
  RegistrationStatus,
  OrderType,
  IconType,
} from "../enums";

const API_BASE_URL = process.env.REACT_APP_TOWERS_API_URL;

const instance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  withCredentials: true,
  xsrfCookieName: "csrftoken",
  xsrfHeaderName: "X-CSRFToken",
  withXSRFToken: true,
  validateStatus: (status: number) => {
    return status >= 200 && status < 500;
  },
});

instance.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    return Promise.reject(error);
  },
);

instance.interceptors.request.use(async (config) => {
  config.headers["Accept-Language"] = i18n.resolvedLanguage;
  config.headers["X-CSRFToken"] = getCookie("csrftoken");
  return config;
});

export const apiUserMe = async () => {
  try {
    return await instance.get("/user/me/");
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiUserLogin = async (email: string, password: string) => {
  try {
    return await instance.post("/user/login/", {
      email: email,
      password: password,
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiUserCreate = async (
  firstname: string,
  lastname: string,
  email: string,
  phone: string,
  password: string,
  password2: string,
  birthday: string,
  consent_pictures: boolean,
  preferred_language: string,
  consentTypes: number[],
  /*
    height_shoulders: number,
    height_arms: number,
  */
) => {
  try {
    return await instance.post("/user/", {
      firstname: firstname,
      lastname: lastname,
      email: email,
      phone: phone,
      password: password,
      password2: password2,
      birthday: birthday,
      consent_pictures: consent_pictures,
      preferred_language: preferred_language,
      towers: {
        /*
          height_shoulders: height_shoulders,
          height_arms: height_arms,
        */
      },
      organisation: {},
      consent_types: consentTypes,
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiUserUpdate = async (
  id: string,
  firstname: string,
  lastname: string,
  phone: string,
  birthday: string,
  consent_pictures: boolean,
  preferred_language: string,
  consentTypes: number[],
  /*
    height_shoulders: number,
    height_arms: number,
  */
) => {
  try {
    return await instance.patch("/user/" + id + "/", {
      firstname: firstname,
      lastname: lastname,
      phone: phone,
      birthday: birthday,
      consent_pictures: consent_pictures,
      preferred_language: preferred_language,
      towers: {
        /*
          height_shoulders: height_shoulders,
          height_arms: height_arms,
        */
      },
      organisation: {},
      consent_types: consentTypes,
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiUserLogout = async () => {
  try {
    return await instance.post("/user/logout/");
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiUserFamily = async (token: string = undefined) => {
  try {
    return await instance.get("/user/family/", { params: { token: token } });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiUserFamilyMemberCreate = async (
  firstname: string,
  lastname: string,
  birthday: string,
  /*
    height_shoulders: number,
    height_arms: number,
  */
  consentTypes: number[],
) => {
  try {
    return await instance.post("/user/family/member/", {
      firstname: firstname,
      lastname: lastname,
      birthday: birthday,
      towers: {
        /*
          height_shoulders: height_shoulders,
          height_arms: height_arms,
        */
      },
      organisation: {},
      consent_types: consentTypes,
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiUserFamilyMemberUpdate = async (
  id: string,
  firstname: string,
  lastname: string,
  birthday: string,
  /*
    height_shoulders: number,
    height_arms: number,
  */
  consentTypes: number[],
) => {
  try {
    return await instance.put("/user/family/member/" + id + "/", {
      firstname: firstname,
      lastname: lastname,
      birthday: birthday,
      towers: {
        /*
          height_shoulders: height_shoulders,
          height_arms: height_arms,
        */
      },
      organisation: {},
      consent_types: consentTypes,
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiUserFamilyMemberDelete = async (id: string) => {
  try {
    return await instance.delete("/user/family/member/" + id + "/");
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiUserVerify = async (token: string) => {
  try {
    return await instance.post("/user/verify/", { token: token });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiUserRequestVerify = async (email: string) => {
  try {
    return await instance.post("/user/request-verify/", { email: email });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiUserPassword = async (
  token: string,
  password: string,
  password2: string,
) => {
  try {
    return await instance.post("/user/password/", {
      token: token,
      password: password,
      password2: password2,
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiUserList = async () => {
  try {
    return await instance.get("/user/");
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiUserRequestPassword = async (email: string) => {
  try {
    return await instance.post("/user/request-password/", { email: email });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiMembershipList = async () => {
  try {
    return await instance.get("/membership/");
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiMembershipRenewList = async () => {
  try {
    return await instance.get("/membership/renew/");
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiMembershipRenewCreate = async (modules: number[]) => {
  try {
    return await instance.post("/membership/renew/", {
      modules: modules,
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiUserFamilyMemberRequestCreate = async (email: string) => {
  try {
    return await instance.post("/user/family/member/request/", {
      email: email,
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiUserFamilyMemberRequestList = async () => {
  try {
    return await instance.get("/user/family/member/request/");
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiUserFamilyMemberRequestReceivedList = async () => {
  try {
    return await instance.get("/user/family/member/request/received/");
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiUserFamilyMemberRequestCancel = async (id: string) => {
  try {
    return await instance.delete(
      "/user/family/member/request/" + id + "/cancel/",
    );
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiUserFamilyMemberRequestReject = async (id: string) => {
  try {
    return await instance.delete(
      "/user/family/member/request/" + id + "/reject/",
    );
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiUserFamilyMemberRequestAccept = async (id: string) => {
  try {
    return await instance.post(
      "/user/family/member/request/" + id + "/accept/",
    );
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiOrderList = async (
  page: number = undefined,
  filterTypes: number[] = undefined,
) => {
  try {
    return await instance.get("/order/", {
      params: {
        page_size: API_ORDERS_LIST_PAGE_SIZE,
        page: page,
        filter_types: filterTypes && filterTypes.join(","),
      },
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiOrderRetrieve = async (id: string) => {
  try {
    return await instance.get("/order/" + id);
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiPaymentProviderList = async () => {
  try {
    return await instance.get("/payment/provider/");
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiPaymentList = async (page: number = undefined) => {
  try {
    return await instance.get("/payment/", {
      params: {
        page_size: API_PAYMENTS_LIST_PAGE_SIZE,
        page: page,
      },
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiPaymentExpenseList = async (page: number = undefined) => {
  try {
    return await instance.get("/payment/expense/", {
      params: {
        page_size: API_EXPENSES_LIST_PAGE_SIZE,
        page: page,
      },
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiEventList = async (
  page: number = undefined,
  pageSize: number = undefined,
  token: string = undefined,
  dateFrom: string = undefined,
  dateTo: string = undefined,
  withCounts: boolean = undefined,
  forMusicians: boolean = undefined,
  filterTypes: number[] = undefined,
  filterIsRegistered: boolean = undefined,
  orderBy: string[] = undefined,
) => {
  try {
    return await instance.get("/event/", {
      params: {
        page_size: pageSize ? pageSize : API_EVENTS_LIST_PAGE_SIZE,
        page: page ? page : 1,
        token: token,
        date_from:
          dateFrom !== undefined
            ? dateFrom
            : new Date().toISOString().substring(0, 10),
        date_to: dateTo,
        with_counts: withCounts,
        for_musicians: forMusicians,
        filter_types: filterTypes && filterTypes.join(","),
        filter_is_registered: filterIsRegistered,
        order_by: orderBy && orderBy.join(","),
      },
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiEventPage = async (
  date: string = undefined,
  code: string = undefined,
  token: string = undefined,
) => {
  try {
    return await instance.get("/event/page/", {
      params: token
        ? { token: token }
        : {
            date: date,
            code: code,
          },
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiEventCalendarList = async (
  dateFrom: string,
  dateTo: string,
) => {
  try {
    return await instance.get("/event/calendar/", {
      params: { date_from: dateFrom, date_to: dateTo },
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiEventRegistrationCreate = async (
  userId: string,
  eventId: string,
  token: string = undefined,
  status: RegistrationStatus = undefined,
) => {
  try {
    return await instance.post("/event/registration/", {
      user_id: userId,
      event_id: eventId,
      token: token,
      status: status,
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiEventRegistrationForEntityCreate = async (
  userId: string = undefined,
  entityId: string = undefined,
  ownerId: string = undefined,
  eventId: string,
  entityFirstname: string = undefined,
  entityLastname: string = undefined,
  entityEmail: string = undefined,
  entityPhone: string = undefined,
  entityBirthday: string = undefined,
  entityLanguage: string = undefined,
  token: string = undefined,
  status: RegistrationStatus = undefined,
  data: any = undefined,
) => {
  try {
    return await instance.post("/event/registration/", {
      ...(userId
        ? { user_id: userId }
        : entityId
          ? { entity_id: entityId }
          : {
              entity: {
                firstname: entityFirstname,
                lastname: entityLastname,
                ...(entityEmail ? { email: entityEmail } : {}),
                ...(entityPhone ? { phone: entityPhone } : {}),
                preferred_language: entityLanguage,
                ...(entityBirthday ? { birthday: entityBirthday } : {}),
              },
            }),
      ...(ownerId ? { owner_id: ownerId } : {}),
      event_id: eventId,
      token: token,
      status: status,
      data: data,
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiEventRegistrationDelete = async (
  registrationId: string,
  token: string = undefined,
) => {
  try {
    return await instance.delete(
      "/event/registration/" + registrationId + "/",
      { params: { token: token } },
    );
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiEventRegistrationList = async (page: number = undefined) => {
  try {
    return await instance.get("/event/registration/", {
      params: {
        page_size: API_REGISTRATIONS_LIST_PAGE_SIZE,
        page: page,
      },
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiLegalTeamList = async () => {
  try {
    return await instance.get("/legal/team/");
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiLegalGroupList = async (page: number = undefined) => {
  try {
    return await instance.get("/legal/group/", {
      params: {
        page_size: API_LEGAL_GROUP_LIST_PAGE_SIZE,
        page: page,
      },
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiLegalBylawsList = async () => {
  try {
    return await instance.get("/legal/bylaws/");
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiMediaReleaseList = async (page: number = undefined) => {
  try {
    return await instance.get("/media/release/", {
      params: {
        page_size: API_MEDIA_PRESS_LIST_PAGE_SIZE,
        page: page,
      },
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiMediaReleaseRetrieve = async (slug: string) => {
  try {
    return await instance.get("/media/release/" + slug);
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiTowersCastleList = async (eventId: string) => {
  try {
    return await instance.get("/towers/castle/", {
      params: { event_id: eventId },
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiProductList = async (page: number = undefined) => {
  try {
    return await instance.get("/product/", {
      params: {
        page_size: API_PRODUCTS_LIST_PAGE_SIZE,
        page: page,
      },
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiOrderCreate = async (
  sizes: any[],
  delivery: any,
  deliveryProvider: any,
  userData: any,
  formPickupData: any,
) => {
  try {
    return await instance.post("/order/", {
      cart: { sizes: sizes },
      delivery: {
        address: delivery,
        provider: { id: deliveryProvider.id, type: deliveryProvider.type },
      },
      user: userData,
      pickup: { event_id: formPickupData.event },
      type: OrderType.PRODUCT,
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiOrderMembershipCreate = async (modules: any[]) => {
  try {
    return await instance.post("/order/", {
      cart: { modules: modules },
      type: OrderType.MEMBERSHIP,
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiOrderEventCreate = async (
  eventRegistrations: any[],
  userData: any,
) => {
  try {
    return await instance.post("/order/", {
      cart: { event_registrations: eventRegistrations },
      user: userData,
      type: OrderType.REGISTRATION,
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiOrderDelete = async (orderId: string) => {
  try {
    return await instance.delete("/order/" + orderId + "/");
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiOrderProviderUpdate = async (
  orderId: string,
  providerId: string,
) => {
  try {
    return await instance.patch("/order/" + orderId + "/provider/", {
      provider_id: providerId,
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiOrderComplete = async (
  orderId: string,
  datePaid: string = undefined,
  transactionId: string = undefined,
  transactionReference: string = undefined,
) => {
  try {
    return await instance.post("/order/" + orderId + "/complete/", {
      date_paid: datePaid,
      transaction: {
        id: transactionId,
        reference: transactionReference,
      },
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiOrderRestart = async (orderId: string) => {
  try {
    return await instance.post("/order/" + orderId + "/restart/");
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiDataLocationCountryList = async () => {
  try {
    return await instance.get("/data/location/country/");
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiOrderDeliveryProviderList = async () => {
  try {
    return await instance.get("/order/delivery/provider/");
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiAdminUserList = async (
  page: number = undefined,
  pageSize: number = undefined,
  ordering: string = undefined,
  isAdult: boolean = undefined,
) => {
  try {
    return await instance.get("/admin/user/", {
      params: {
        page_size: pageSize ? pageSize : API_ADMIN_USER_LIST_PAGE_SIZE,
        page: page,
        ordering: ordering,
        is_adult: isAdult,
      },
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiAdminUserRetrieve = async (userId: string = undefined) => {
  try {
    return await instance.get("/admin/user/" + userId);
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiAdminOrderList = async (
  page: number = undefined,
  pageSize: number = undefined,
) => {
  try {
    return await instance.get("/admin/order/", {
      params: {
        page_size: pageSize ? pageSize : API_ADMIN_ORDER_LIST_PAGE_SIZE,
        page: page,
      },
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiAdminEventList = async (
  page: number = undefined,
  pageSize: number = undefined,
  dateFrom: string = undefined,
  dateTo: string = undefined,
  forMusicians: boolean = undefined,
) => {
  try {
    return await instance.get("/admin/event/", {
      params: {
        page_size: pageSize ? pageSize : API_ADMIN_EVENT_LIST_PAGE_SIZE,
        page: page,
        date_from: dateFrom,
        date_to: dateTo,
        for_musicians: forMusicians,
      },
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiAdminEventRegistrationList = async (
  page: number = undefined,
  pageSize: number = undefined,
  eventId: string,
) => {
  try {
    return await instance.get("/admin/event/registration/", {
      params: {
        page_size: pageSize ? pageSize : API_ADMIN_REGISTRATION_LIST_PAGE_SIZE,
        page: page,
        event_id: eventId,
      },
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiAdminTowersEventList = async (eventId: string) => {
  try {
    return await instance.get("/admin/towers/event/" + eventId);
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiAdminTowersStatsPositionList = async (
  page: number = undefined,
  pageSize: number = undefined,
) => {
  try {
    return await instance.get("/admin/towers/stats/position", {
      params: {
        page_size: pageSize
          ? pageSize
          : API_ADMIN_TOWERS_STATS_POSITIONS_PAGE_SIZE,
        page: page,
      },
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiAdminUserUpdate = async (
  userId: string,
  alias: string = undefined,
  heightShoulders: string = undefined,
  heightArms: string = undefined,
) => {
  try {
    return await instance.patch("/admin/user/" + userId + "/", {
      towers: {
        alias: alias,
        height_shoulders: heightShoulders,
        height_arms: heightArms,
      },
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiAdminHistoryEventList = async (
  page: number = undefined,
  pageSize: number = undefined,
) => {
  try {
    return await instance.get("/admin/history/event", {
      params: {
        page_size: pageSize ? pageSize : API_ADMIN_HISTORY_EVENT_PAGE_SIZE,
        page: page,
      },
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiAdminHistoryEventUpdate = async (
  id: string,
  date: string,
  title: Object,
  description: Object,
  iconId: string,
) => {
  try {
    return await instance.patch("/admin/history/event/" + id + "/", {
      date: date,
      title: title,
      description: description,
      icon_id: iconId,
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiAdminHistoryEventCreate = async (
  date: string,
  title: Object,
  description: Object,
  iconId: string,
) => {
  try {
    return await instance.post("/admin/history/event/", {
      date: date,
      title: title,
      description: description,
      icon_id: iconId,
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiAdminHistoryEventDelete = async (id: string) => {
  try {
    return await instance.delete("/admin/history/event/" + id + "/");
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiAdminThemeIconList = async (
  page: number = undefined,
  pageSize: number = undefined,
) => {
  try {
    return await instance.get("/admin/theme/icon", {
      params: {
        page_size: pageSize ? pageSize : API_ADMIN_THEME_ICON_PAGE_SIZE,
        page: page,
        filter_types: IconType.MUI_MATERIAL_ICON,
      },
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiAdminEventTokenGet = async (
  eventId: string,
  signupIsOpen: boolean = undefined,
  signupIsFull: boolean = undefined,
) => {
  try {
    return await instance.get("/admin/event/" + eventId + "/token/", {
      params: {
        ...(signupIsOpen !== undefined
          ? { signup_is_open: signupIsOpen, signup_is_full: signupIsFull }
          : {}),
      },
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiDocumentList = async (
  page: number = undefined,
  pageSize: number = undefined,
  filterTypes: number[] = undefined,
  orderBy: string[] = undefined,
) => {
  try {
    return await instance.get("/document/", {
      params: {
        page_size: pageSize ? pageSize : API_DOCUMENTS_LIST_PAGE_SIZE,
        page: page,
        filter_types: filterTypes && filterTypes.join(","),
        order_by: orderBy && orderBy.join(","),
      },
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiContactMessageCreate = async (
  entity: { firstname: string; lastname: string; email: string },
  type: ContactMessageType,
  message: string,
) => {
  try {
    return await instance.post("/contact/message/", {
      entity: entity,
      type: type,
      message: message,
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiHistoryEventGroupList = async (page: number = undefined) => {
  try {
    return await instance.get("/history/event/", {
      params: {
        page_size: API_HISTORY_EVENT_LIST_PAGE_SIZE,
        page: page,
      },
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiActivityProgramCourseRegistrationList = async (
  page: number = undefined,
  pageSize: number = undefined,
) => {
  try {
    return await instance.get("/activity/program/course/registration/", {
      params: {
        page_size: pageSize
          ? pageSize
          : API_PROGRAM_COURSE_REGISTRATIONS_LIST_PAGE_SIZE,
        page: page,
      },
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiNewsletterList = async (
  page: number = undefined,
  pageSize: number = undefined,
) => {
  try {
    return await instance.get("/notify/newsletter/", {
      params: {
        page_size: pageSize ? pageSize : API_NEWSLETTER_LIST_PAGE_SIZE,
        page: page,
      },
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiConsentEntityList = async (
  page: number = undefined,
  pageSize: number = undefined,
) => {
  try {
    return await instance.get("/consent/entity/", {
      params: {
        page_size: pageSize ? pageSize : API_CONSENT_ENTITY_LIST_PAGE_SIZE,
        page: page,
      },
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiConsentEntityCreate = async (
  firstname: string,
  lastname: string,
  email: string,
  phone: string,
  consents: any[],
) => {
  try {
    return await instance.post("/consent/entity/", {
      entity: email && {
        firstname: firstname,
        lastname: lastname,
        email: email,
        phone: phone,
      },
      consents: consents.map((consent: any) => {
        return {
          type: consent.type,
          newsletter_id: consent.newsletterId,
          is_active: consent.isActive,
        };
      }),
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiIntegrationGoogleWalletPassLoyaltyGet = async () => {
  try {
    return await instance.get("integration/google/wallet/pass/loyalty/");
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiIntegrationGoogleWalletPassEventGet = async (
  registrationId: string,
) => {
  try {
    return await instance.get(
      "integration/google/wallet/pass/event/" + registrationId + "/",
    );
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiIntegrationAppleWalletPassLoyaltyGet = async () => {
  try {
    return await instance.get("integration/apple/wallet/pass/loyalty/", {
      responseType: "blob",
    });
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiAdminStatsEventGet = async (eventId: string) => {
  try {
    return await instance.get("/admin/stats/event/" + eventId + "/");
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiAdminEventRegistrationsGet = async (
  page: number = undefined,
  pageSize: number = undefined,
  eventId: string,
) => {
  try {
    return await instance.get(
      "/admin/event/registration/?event_id=" + eventId,
      {
        params: {
          page_size: pageSize
            ? pageSize
            : API_ADMIN_EVENT_REGISTRATION_PAGE_SIZE,
          page: page,
        },
      },
    );
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiAdminEventRegistrationsSearch = async (
  eventId: string,
  search: string,
) => {
  try {
    return await instance.get(
      "/admin/event/registration/search/?event_id=" +
        eventId +
        "&search=" +
        search,
    );
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiAdminEventRegistrationUpdate = async (
  registrationId: string,
  hasAttended: boolean = undefined,
) => {
  try {
    return await instance.patch(
      "/admin/event/registration/" + registrationId + "/",
      {
        ...(hasAttended !== undefined ? { has_attended: hasAttended } : {}),
      },
    );
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};

export const apiAdminEventGet = async (eventId: string) => {
  try {
    return await instance.get("/admin/event/" + eventId + "/");
  } catch (error) {
    console.error("Error fetching data: ", error);
    // Handle errors here or throw them to be handled where the function is called
    throw error;
  }
};
